export interface ApplicationConfigState {
	/**
	 * If the application config request is loading.
	 */
	loading: boolean;
	/**
	 * If the application config request has already been loaded.
	 */
	loaded: boolean;
	/**
	 * If there was an error in the request to get the configs.
	 */
	errorLoading: boolean;
	/**
	 * The actual application configs, if loaded.
	 */
	configs: ApplicationConfigs;
	/**
	 * I think this can be deleted.
	 */
	[key: string]: any;
}

export interface ApplicationConfigs {
	/**
	 * Our adyen client key
	 */
	adyenClientKey?: string;
	/**
	 * Accesso ID configuration
	 */
	accesso_id?: AccessoIdConfig;
	/**
	 * The accesso Pay configuration
	 */
	accessoPay?: AppConfigAccessoPay;
	/**
	 * The location where operations-created images are configured.
	 */
	assetsPath?: string;
	/**
	 * Enables contract viewer
	 * enable_new_contract_viewer is a new feature flag
	 * to enable the new contract viewer inside the application
	 */
	contract_viewer?: ContractViewerConfig;
	/**
	 * The cybersource session timeout in milliseconds
	 */
	cybersource_session_timeout?: number;
	/**
	 * The currency code override
	 */
	currency_code?: string;

	/**
	 * Override for CSS variables used in guest-ui lib
	 */
	css_variables?: string;

	/**
	 * The default country code to use with country dropdowns
	 */
	default_country_code?: string;
	/**
	 * The default phone code to use with phone numbers
	 */
	default_phone_code?: string;
	/**
	 * If we should disable the faq tab in the navigation
	 */
	disable_faq_tab?: boolean;
	/**
	 * If we should disable the membership tab in the navigation
	 */
	disable_membership_tab?: boolean;
	/**
	 * If you want to disable cancellations on the membership tab
	 */
	disable_membership_cancelation?: boolean;
	/**
	 * If you want to disable pay offs on the membership tab
	 */
	disable_membership_pay_off?: boolean;
	/**
	 * If you want to disable the minimum phone length
	 */
	disable_min_phone_length?: boolean;
	/**
	 * If you want to disable the payment history tab in the navigation
	 */
	disable_payment_history_tab?: boolean;
	/**
	 * If you want to disable the payment_id displayed on the Payment History page
	 */
	disable_payment_id?: boolean;
	/**
	 * If you want to disable the zip code validation
	 */
	disable_zip_code_validation?: boolean;
	/**
	 * If is enabled the enhaced phone number. This property actually comes from the App config response.
	 */
	enhanced_phone_number?: EnhancedPhoneNumber;
	/**
	 * The logo file name
	 */
	logo?: string;
	/**
	 * MFA Resend Code Wait Timer
	 */
	mfa_resend_code_wait_time?: number;
	/**
	 * The primary navigation background HEX color
	 */
	primary_nav_color?: string;
	/**
	 * The primary navigation text HEX color
	 */
	primary_nav_text_color?: string;
	/**
	 * URLs for printing (ex: tickets)
	 */
	print_path?: {
		development?: string;
		test?: string;
		staging?: string;
		production?: string;
	};
	/**
	 * URL to send to MCE for mobile printing
	 */
	reprint_mobile_link?: string;
	/**
	 * The receipt configuration
	 */
	receipts?: {
		/**
		 * if true, hide the receipt navigation item
		 */
		disable_receipts_nav_item?: boolean;
		/**
		 * The receipt tenant name
		 */
		receipt_tenant?: string;
	};
	/**
	 * If you want to show payment history footer
	 */
	show_payment_history_footer?: boolean;
	/**
	 * If we support 2nd payments. This property actually comes from the PaymentPortalLogin response.
	 */
	supportSecondPayment?: boolean;
	/**
	 * If merchant support multiple locales. This property actually comes from the PaymentPortalLogin response.
	 */
	supported_languages?: string;
	/**
	 * Session time for the Portal before autoLogout
	 * There will be a prompt to continue session 1 minute before end of session
	 * If continued entire session length will be renewed
	 */
	session_time?: number;
	/**
	 * Allow Lock Out Access will provide the user limited access
	 * to the portal when otherwise locked out
	 */
	allow_lock_out_access?: boolean;
	/**
	 * If allow lock out access, and would like to enable payments
	 * this comma delimited list of codes
	 * will allow payment for codes listed
	 */
	enable_payments_lock_out_codes?: number | string;
	/**
	 * Booking Portal Configuration
	 */
	bookings?: BookingsConfig;
	/**
	 * Optional override for the favicon image
	 * and must be saved in the assetsPath location
	 */
	faviconOverride?: string;
	/**
	 * Optional suffix for overriding the locales per merchant
	 */
	merchant_locale_suffix?: string;
	/**
	 * Optional config for value to use to replace all locale instances of @@supportNumber with
	 */
	supportNumber?: string;
	/**
	 * Optional config to order the navigation tree
	 */
	navigation_tree_display?: string[];
	/**
	 * Optional config to refresh the SiriusWare getTicketUsage call every x seconds
	 */
	sw_ticket_usage_refresh_time?: number;
	/**
	 * Optional config to enable compulsory payment details to show on Overview
	 */
	enable_compulsory_payment_details?: boolean;
}

export const initialState: ApplicationConfigState = {
	loaded: false,
	loading: false,
	errorLoading: false,
	configs: {},
};

export interface AppConfigAccessoPay {
	enable: boolean;
	tenant?: string;
	merchantId?: string;
	language?: string;
	scriptUrl?: string;
	disableCardTypes?: string[];
	disablePaymentTypes?: string[];
	merchantOverrideParam?: string;
	requireDemographics?: boolean;
}

export interface BookingsConfig {
	enableBookings: boolean;
	accessoPay?: AppConfigAccessoPay;
	capture_prompt_email_list?: string[];
	booking_tax_inclusive_pricing?: boolean;
	display_modifications_remaining?: boolean;
	display_zero_capacity?: boolean;
	enableCancellation?: boolean;
	enableEmailItinerary?: boolean;
	enableViewTickets?: boolean;
	email_receipt_all_booking_modifications?: boolean;
	experience_options?: BookingExperienceOptions;
	mode?: string;
	show_free_days_label?: boolean;
	bypass_booking_upgrade_terms_and_conditions_for_even_price_exchanges?: boolean;
}

export interface BookingExperienceOptions {
	enable_conflict_resolution?: boolean;
	vendor_time_buffers?: VendorItems[];
}

export interface VendorItems {
	buffer_minutes: number;
	vendor_id_1: string;
	vendor_id_2: string;
}

export interface ContractViewerConfig {
	enable: boolean;
	path: string;
	enable_new_contract_viewer?: boolean;
}

export interface AccessoIdConfig {
	enable: boolean;
	show_legacy_option: boolean;
	show_legacy_sign_in?: boolean;
}
